import { Variable } from 'src/api/types';
import {
  findVariableByToken,
  getFormulaVariableTokens,
  isWorkspaceOrGlobalVariable,
  normalizeVariableToken,
} from 'src/components/FormulaInput/shared/formula';

export interface MissingVariableEntry {
  /** Resolved-but-unpopulated variable, or null for an unresolved token (a variable that does not exist yet). */
  variable: Variable | null;
  /** The raw token as referenced in the formula (id, name or alias form, without '#'). */
  token: string;
  displayName: string;
}

export interface MissingVariableValue {
  entry: MissingVariableEntry;
  value: number;
  currency: boolean;
}

const prettifyToken = (token: string): string => {
  const display = token.replace(/^#/, '').replace(/_/g, ' ');
  return display.charAt(0).toUpperCase() + display.slice(1);
};

interface VariableUpsertEntry {
  id?: number;
  name: string;
  value: number;
  currency: boolean;
  isGlobal: boolean;
}

/**
 * Scope-split of entered values, mirroring formula resolution: assessment variables
 * (incl. overrides of workspace ones) must be saved to the assessment — writing the
 * workspace sibling instead would leave the formula unresolved, because the override
 * (lower id, same name) wins at evaluation time. Unresolved tokens become new
 * workspace custom variables named after the entry's display name (name-normalized
 * resolution matches them back to the token).
 */
export const splitMissingVariableValues = (
  values: MissingVariableValue[],
  hasAssessmentScope: boolean,
): { workspaceVariables: VariableUpsertEntry[]; assessmentVariables: VariableUpsertEntry[] } => {
  const workspaceVariables: VariableUpsertEntry[] = [];
  const assessmentVariables: VariableUpsertEntry[] = [];

  values.forEach(({ entry, value, currency }) => {
    const variable = entry.variable;
    if (variable && !isWorkspaceOrGlobalVariable(variable) && hasAssessmentScope) {
      assessmentVariables.push({ id: variable.id, name: variable.name, value, currency, isGlobal: false });
    } else if (variable) {
      workspaceVariables.push({ id: variable.id, name: variable.name, value, currency, isGlobal: variable.isGlobal });
    } else {
      workspaceVariables.push({ name: entry.displayName, value, currency, isGlobal: false });
    }
  });

  return { workspaceVariables, assessmentVariables };
};

/**
 * Union of missing variables across a set of formulas (nudge detection). A variable
 * is missing when its token is unresolved or resolves to a valueless variable —
 * resolution is override-aware and accepts id, name and alias tokens, so bare-name
 * template formulas are detected too. Deduplicated by resolved variable id, or by
 * normalized token for unresolved names.
 */
export const buildMissingVariableEntries = (
  formulas: (string | number | null | undefined)[],
  variables: Variable[],
): MissingVariableEntry[] => {
  const entries: MissingVariableEntry[] = [];
  const seen = new Set<string>();

  formulas.forEach((formula) => {
    getFormulaVariableTokens(formula).forEach((token) => {
      const variable = findVariableByToken(variables, token);
      if (variable && variable.value != null) return;

      const key = variable ? `id:${variable.id}` : `token:${normalizeVariableToken(token)}`;
      if (seen.has(key)) return;
      seen.add(key);

      entries.push({
        variable: variable ?? null,
        token,
        displayName: variable ? (variable.alias ?? variable.name) : prettifyToken(token),
      });
    });
  });

  return entries;
};
